import { PageSize } from '@/constant';
import { TDataStep } from '@/pages/workers/dm1-02/type';
import {
	GetWorkerDocumentListParams,
	IDocument,
	TDocumentDefineItem,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '..';
import {
	formatDocumentDetail,
	formatWorkerDocumentListV2,
} from './worker-document.helper';
import {
	IPayloadDeleteWorkerDocumentRequest,
	IPayloadGetWorkerDocumentDetailSuccess,
	IPayloadGetWorkerDocumentListRequest,
	IPayloadGetWorkerDocumentListSuccess,
	IPayloadUpdateDocumentRequest,
	IPayloadWorkerDocumentDetailRequest,
	IWorkerDocumentDetailStore,
} from './worker-document.type';

const InitParams: GetWorkerDocumentListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	workerDocumentList: [] as IWorkerDocumentDetailStore[],
	total: 0,
	params: InitParams,
	documentDetail: {} as TDocumentDefineItem,
	dataStep: {} as TDataStep | null,
};

const workerDocumentSlice = createSlice({
	initialState,
	name: 'workerDocument',
	reducers: {
		/**---- GET WORKER DOCUMENT LIST START ---- */
		getWorkerDocumentListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerDocumentListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getWorkerDocumentListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerDocumentListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.workerDocumentList = content;
			state.total = total;
		},
		getWorkerDocumentListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.workerDocumentList = [];
		},
		/**---- GET WORKER DOCUMENT LIST END ---- */

		/**---- DELETE WORKER DOCUMENT START ---- */
		deleteWorkerDocumentRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerDocumentRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerDocumentSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerDocumentFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER DOCUMENT START ---- */
		/**---- UPDATE WORKER DOCUMENT START ---- */
		updateWorkerDocumentSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IDocument>>
		) => {
			const { data } = action.payload;
			const workerDocumentList = [...state.workerDocumentList];
			state.workerDocumentList = formatWorkerDocumentListV2(
				mergeListItemUpdated<IWorkerDocumentDetailStore, IDocument>(
					workerDocumentList,
					data
				)
			);
		},
		/**---- UPDATE WORKER DOCUMENT END ---- */
		/**---- GET WORKER DOCUMENT DETAIL START ---- */
		getWorkerDocumentDetailRequest: (
			state,
			action: PayloadAction<IPayloadWorkerDocumentDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerDocumentDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerDocumentDetailSuccess>
		) => {
			state.fetching = false;
			state.documentDetail = action.payload.documentDetail;
		},
		getWorkerDocumentDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET WORKER DOCUMENT DETAIL END ---- */
		/**---- UPDATE WORKER DOCUMENT DETAIL START ---- */
		updateWorkerDocumentDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<TDocumentDefineItem>>
		) => {
			const { data } = action.payload;
			const workerDocumentDetail = { ...state.documentDetail };
			state.documentDetail = formatDocumentDetail(
				mergeItemUpdated<TDocumentDefineItem, TDocumentDefineItem>(
					workerDocumentDetail,
					data
				)
			);
		},
		/**---- UPDATE WORKER DOCUMENT START ---- */
		updateDocumentRequest: (
			state,
			action: PayloadAction<IPayloadUpdateDocumentRequest>
		) => {
			state.fetching = true;
		},
		updateDocumentSuccess: (state) => {
			state.fetching = false;
		},
		updateDocumentFailure: (state) => {
			state.fetching = false;
		},
		/**---- UPDATE WORKER DOCUMENT END ---- */

		handleNextStep: (state, action: PayloadAction<TDataStep | null>) => {
			state.dataStep = action.payload;
		},
	},
});

export const WorkerDocumentSelectors = {
	selectFetching: (state: RootState) => state.workerDocument.fetching,
	selectParams: (state: RootState) => state.workerDocument.params,
	selectList: (state: RootState) => state.workerDocument.workerDocumentList,
	selectTotal: (state: RootState) => state.workerDocument.total,
	selectDocumentDetail: (state: RootState) =>
		state.workerDocument.documentDetail,
	selectDataStep: (state: RootState) => state.workerDocument.dataStep,
};

export const WorkerDocumentActions = workerDocumentSlice.actions;
export const WorkerDocumentReducer = workerDocumentSlice.reducer;
